// Pile setting-out schedule — the first bridge site report (docs/digitwin/Bridges Roadmap.md, Phase 8).
//
// One row per pile: support, grid Easting/Northing (for the surveyor) and station/offset on the
// bridge's own design axis (for engineers to check), cut-off and toe levels, length, diameter,
// material. Every value is OBM's own (CifBridge pile aspects, via GET /api/bridges/.../piles); only
// station/offset are computed, with the viewer's exact alignment projection (pileSchedule.js).
// Excel cells hold the full-precision values, formatted to the millimetre.
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Table, Button, Spin, Alert } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { formatStation } from "../viewer/viewerAlignment.js";
import { findBridgeAxis, stationOffset } from "./pileSchedule.js";

const MONO = "'DM Mono', monospace";
const m3 = (v) => (v == null ? "—" : v.toFixed(3));
const natural = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

export default function PileScheduleModal({ open, onClose, file, isMobile, alignList, fetchAlignments }) {
  const [data, setData] = useState(null);   // { piles, supportLines } | { error }
  const [bridge, setBridge] = useState(null);

  // Load once per file, the first time the report is opened
  useEffect(() => { setData(null); setBridge(null); }, [file]);
  useEffect(() => {
    if (!open || data) return;
    fetchAlignments();
    fetch(`/api/bridges/${file.projectId}/${encodeURIComponent(file.fileName)}/piles`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setData(d))
      .catch((e) => setData({ error: e.message }));
  }, [open, data, file, fetchAlignments]);

  const bridges = useMemo(() => [...new Set((data?.piles || []).map((p) => p.bridge))].sort(natural), [data]);
  useEffect(() => { if (!bridge && bridges.length) setBridge(bridges[0]); }, [bridges, bridge]);

  const axis = useMemo(() => (bridge && data?.supportLines && alignList
    ? findBridgeAxis(data.supportLines[bridge], alignList) : null), [bridge, data, alignList]);

  const rows = useMemo(() => (data?.piles || []).filter((p) => p.bridge === bridge).map((p) => ({
    ...p, key: p.elementId,
    ...(axis && p.x != null ? stationOffset(p.x, p.y, axis.tess, axis.startChainage) : { station: null, offset: null }),
  })), [data, bridge, axis]);

  const supports = new Set(rows.map((r) => r.support)).size;
  const totalLength = rows.reduce((s, r) => s + (r.length || 0), 0);
  const axisPending = alignList === null;

  const downloadExcel = () => {
    const headers = ["Bridge", "Support", "Pile", "Easting (m)", "Northing (m)", "Station (m)", "Offset (m)",
      "Cut-off level (m)", "Toe level (m)", "Length (m)", "Diameter (m)", "Shape", "Material"];
    const body = rows.map((r) => [r.bridge, r.support, r.name, r.x, r.y, r.station, r.offset,
      r.cutOff, r.toe, r.length, r.diameter, r.shape, r.material]);
    const ws = XLSX.utils.aoa_to_sheet([
      [`Pile setting-out schedule — ${bridge}`],
      [axis ? `Station/offset on ${axis.alignment.name}, chainage ${formatStation(axis.startChainage)} at its start; offset left negative` : "Station/offset not available (bridge axis not found)"],
      [],
      headers, ...body,
    ]);
    // Full-precision values, shown to the millimetre
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = 4; R <= range.e.r; R++) {
      for (const C of [3, 4, 5, 6, 7, 8, 9, 10]) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && cell.t === "n") cell.z = "0.000";
      }
    }
    ws["!cols"] = headers.map((h, i) => ({ wch: i === 12 ? 30 : Math.max(10, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Piles");
    XLSX.writeFile(wb, `Pile_Schedule_${bridge}.xlsx`);
  };

  const num = (title, key, fmt = m3, width = 96) => ({
    title, dataIndex: key, key, width, align: "right",
    render: (v) => <span style={{ fontFamily: MONO, fontVariantNumeric: "tabular-nums" }}>{fmt(v)}</span>,
  });
  const columns = [
    { title: "Support", dataIndex: "support", key: "support", width: 120, fixed: "left",
      filters: [...new Set(rows.map((r) => r.support))].map((s) => ({ text: s, value: s })),
      onFilter: (v, r) => r.support === v },
    { title: "Pile", dataIndex: "name", key: "name", width: 64, fixed: "left" },
    num("Easting", "x", m3, 118),
    num("Northing", "y", m3, 122),
    num("Station", "station", (v) => (v == null ? "—" : formatStation(v)), 112),
    num("Offset", "offset", m3, 84),
    num("Cut-off", "cutOff"),
    num("Toe", "toe"),
    num("Length", "length", m3, 84),
    num("Ø", "diameter", m3, 70),
    { title: "Material", dataIndex: "material", key: "material", width: 200, ellipsis: true },
  ];

  const loading = open && !data;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={isMobile ? "96vw" : 1240}
      title={<span style={{ fontFamily: MONO, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em" }}>Pile Setting-Out Schedule</span>}
      footer={rows.length > 0 ? (
        <Button icon={<DownloadOutlined />} onClick={downloadExcel} style={{ fontFamily: MONO, fontSize: 12 }}>Download Excel</Button>
      ) : null}
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : data?.error ? (
        <Alert type="error" showIcon message={`Could not load piles: ${data.error}`} />
      ) : bridges.length === 0 ? (
        <div style={{ fontFamily: MONO, fontSize: 12, color: "rgba(255,255,255,0.45)", padding: "12px 0" }}>No OpenBridge piles in this model.</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <Select value={bridge} onChange={setBridge} style={{ width: 160 }} options={bridges.map((b) => ({ value: b, label: b }))} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
              {rows.length} piles · {supports} supports · total length {totalLength.toFixed(3)} m
            </span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 10, lineHeight: 1.6 }}>
            {axisPending ? "Finding the bridge axis…" : axis ? (
              <>Station / offset on the bridge axis <b style={{ color: "rgba(255,255,255,0.8)" }}>{axis.alignment.name}</b>, chainage {formatStation(axis.startChainage)} at its start (from the support lines) · offset left negative · levels and E/N as modelled in OBM</>
            ) : (
              <span style={{ color: "#faad14" }}>Bridge axis not found: station / offset are not shown. E/N and levels are unaffected.</span>
            )}
          </div>
          <Table
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 1180, y: isMobile ? "55vh" : "60vh" }}
            sticky
          />
        </>
      )}
    </Modal>
  );
}
